import React from "react";
import { getUserAuthState } from "../utils/authButtons.js";

/**
 * Welcome Block Component Configuration
 * Modular component that can be used in both puckBundle.js and puckHeaderBundle.js
 *
 * @param {Function} RichTextField - The RichTextField component to use for rich text editing
 * @returns {Object} WelcomeBlock configuration object for Puck
 */
export function createWelcomeBlock(RichTextField) {
  return {
    label: "Welcome Block",
    fields: {
      // Logged In State - Primary Fields
      loggedInWelcomeMessage: {
        type: "text",
        label: "Welcome Message (with {userName} placeholder)",
        placeholder: "Welcome back, {userName}!"
      },
      loggedInDescription: {
        type: "custom",
        label: "Description",
        render: RichTextField
      },
      // Logged In State - Advanced Fields
      loggedInWelcomeFallback: {
        type: "text",
        label: "Welcome Message (no user name)",
        placeholder: "Welcome back!"
      },
      loggedInProfileLinkText: {
        type: "text",
        label: "Profile Page Link Text",
        placeholder: "your profile page"
      },
      loggedInViewProfileButton: {
        type: "text",
        label: "View Profile Button Text",
        placeholder: "View Profile"
      },
      loggedInSignOutButton: {
        type: "text",
        label: "Sign Out Button Text",
        placeholder: "Sign out"
      },
      // Logged Out State - Primary Fields
      loggedOutHeading: {
        type: "text",
        label: "Sign Up Heading",
        placeholder: "Sign up for {hubName}"
      },
      loggedOutDescription: {
        type: "custom",
        label: "Description",
        render: RichTextField
      },
      loggedOutCallToAction: {
        type: "text",
        label: "Call to Action Text",
        placeholder:
          "Create your profile to participate and customize your notification preferences on your profile page."
      },
      // Logged Out State - Advanced Fields
      loggedOutSignUpButton: {
        type: "text",
        label: "Sign Up Button Text",
        placeholder: "Sign up"
      },
      loggedOutSignInPrompt: {
        type: "text",
        label: "Sign In Prompt Text",
        placeholder: "Already have an account?"
      },
      loggedOutSignInLink: {
        type: "text",
        label: "Sign In Link Text",
        placeholder: "Sign in here"
      }
    },
    defaultProps: {
      loggedInWelcomeMessage: "Welcome back, {userName}!",
      loggedInWelcomeFallback: "Welcome back!",
      loggedInDescription:
        '<p>You can view and edit your contact information and notification preferences on <a href="{profileUrl}" class="text-decoration-underline">your profile page</a>.</p>',
      loggedInProfileLinkText: "your profile page",
      loggedInViewProfileButton: "View Profile",
      loggedInSignOutButton: "Sign out",
      loggedOutHeading: "Sign up for {hubName}",
      loggedOutDescription:
        "<p>Create your profile to participate and customize your notification preferences on your profile page.</p>",
      loggedOutCallToAction:
        "Create your profile to participate and customize your notification preferences on your profile page.",
      loggedOutSignUpButton: "Sign up",
      loggedOutSignInPrompt: "Already have an account?",
      loggedOutSignInLink: "Sign in here"
    },
    render: function ({
      loggedInWelcomeMessage,
      loggedInWelcomeFallback,
      loggedInDescription,
      loggedInProfileLinkText,
      loggedInViewProfileButton,
      loggedInSignOutButton,
      loggedOutHeading,
      loggedOutDescription,
      loggedOutCallToAction,
      loggedOutSignUpButton,
      loggedOutSignInPrompt,
      loggedOutSignInLink
    }) {
      // Get hub and customer info for URL generation
      function getHubInfo() {
        const hubId =
          window.modelData?.hubId ||
          window.modelData?.hub?.id ||
          window.modelData?.hub?.Id;
        const customerId =
          window.modelData?.customerId ||
          window.modelData?.customer?.id ||
          window.modelData?.customer?.Id;
        const customerStringId =
          window.modelData?.customer?.stringId ||
          window.modelData?.customer?.StringId;
        const hubName =
          window.modelData?.hub?.name ||
          window.modelData?.hub?.Name ||
          "this hub";

        return { hubId, customerId, customerStringId, hubName };
      }

      const { isLoggedIn, displayName } = getUserAuthState();
      const hubInfo = getHubInfo();
      const currentUrl = window.location?.href || "";

      // Generate URLs
      const profileUrl =
        hubInfo.hubId && hubInfo.customerStringId
          ? `/Profile/${hubInfo.customerStringId}/${hubInfo.hubId}`
          : "#";
      const registerUrl =
        hubInfo.hubId && hubInfo.customerStringId
          ? `/Portal/Register/${hubInfo.customerStringId}/${
              hubInfo.hubId
            }?returnUrl=${encodeURIComponent(currentUrl)}`
          : "#";
      const loginUrl =
        hubInfo.hubId && hubInfo.customerStringId
          ? `/Portal/Login/${hubInfo.customerStringId}/${
              hubInfo.hubId
            }?returnUrl=${encodeURIComponent(currentUrl)}`
          : "#";
      const signOutUrl = `/Account/LogOff?returnUrl=${encodeURIComponent(
        currentUrl
      )}`;

      // Helper function to replace placeholders in text
      function replacePlaceholders(text) {
        if (!text) return "";
        return text
          .replace(/{userName}/g, displayName || "")
          .replace(/{hubName}/g, hubInfo.hubName)
          .replace(/{profileUrl}/g, profileUrl)
          .replace(
            /{profileLinkText}/g,
            loggedInProfileLinkText || "your profile page"
          );
      }

      // Render logged in state
      if (isLoggedIn) {
        const welcomeText =
          displayName && loggedInWelcomeMessage
            ? replacePlaceholders(loggedInWelcomeMessage)
            : loggedInWelcomeFallback || "Welcome back!";

        const descriptionHtml = loggedInDescription
          ? replacePlaceholders(loggedInDescription)
          : `<p class="mt-2">You can view and edit your contact information and notification preferences on <a href="${profileUrl}" class="text-decoration-underline">${
              loggedInProfileLinkText || "your profile page"
            }</a>.</p>`;

        return React.createElement(
          "div",
          {
            id: "profileInfoBlock",
            className: "col-12"
          },
          React.createElement("p", { className: "h3" }, welcomeText),
          descriptionHtml &&
            React.createElement("div", {
              dangerouslySetInnerHTML: { __html: descriptionHtml }
            }),
          React.createElement(
            "a",
            {
              className: "btn btn-primary pi-btn-primary me-2",
              href: profileUrl
            },
            loggedInViewProfileButton || "View Profile"
          ),
          React.createElement(
            "a",
            {
              className: "btn btn-outline-secondary pi-btn-outline-secondary",
              href: signOutUrl
            },
            loggedInSignOutButton || "Sign out"
          )
        );
      }

      // Render logged out state
      const headingText = replacePlaceholders(
        loggedOutHeading || `Sign up for ${hubInfo.hubName}`
      );
      const descriptionHtml = loggedOutDescription
        ? replacePlaceholders(loggedOutDescription)
        : "";
      // Only show callToActionText if descriptionHtml is empty (to avoid duplication)
      const callToActionText =
        (!descriptionHtml || descriptionHtml.trim() === "") &&
        loggedOutCallToAction
          ? loggedOutCallToAction
          : "";

      return React.createElement(
        "div",
        {
          id: "profileInfoBlock",
          className: "col-12"
        },
        React.createElement("p", { className: "h3" }, headingText),
        descriptionHtml &&
          React.createElement("div", {
            dangerouslySetInnerHTML: { __html: descriptionHtml }
          }),
        callToActionText && React.createElement("p", {}, callToActionText),
        React.createElement(
          "a",
          {
            className: "btn btn-primary pi-btn-primary show-sign-up-modal mb-2",
            href: registerUrl,
            rel: "nofollow"
          },
          loggedOutSignUpButton || "Sign up"
        ),
        React.createElement(
          "label",
          { className: "have-an-account" },
          React.createElement(
            "b",
            {},
            loggedOutSignInPrompt || "Already have an account?"
          ),
          " ",
          React.createElement(
            "a",
            {
              className: "text-decoration-underline",
              href: loginUrl,
              rel: "nofollow"
            },
            loggedOutSignInLink || "Sign in here"
          ),
          "."
        )
      );
    }
  };
}
